import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Notice } from '@/components/Modal';
import { useSession } from '@/lib/auth/session';
import { withGuide } from '@/lib/tour/guideLink';
import { useAgentChat } from '@/pages/aiagent/AgentChatProvider';
import { useAgentCapabilities } from '@/pages/aiagent/capabilities';
import { CitationCard } from '@/pages/aiagent/CitationCard';
import { ChoiceCard } from '@/pages/aiagent/ChoiceCard';
import { ConfirmRow } from '@/pages/aiagent/ConfirmRow';
import { FloorplanCard } from '@/pages/aiagent/FloorplanCard';
import { IssuancePreviewCard } from '@/pages/aiagent/IssuancePreviewCard';
import { Markdown } from '@/pages/aiagent/Markdown';
import { MeetingCalendarCard } from '@/pages/aiagent/MeetingCalendarCard';
import { ProbeCard } from '@/pages/aiagent/ProbeCard';
import { ProgressCurveCard } from '@/pages/aiagent/ProgressCurveCard';
import type { AgentChatMessage, AgentChatRating } from '@/pages/aiagent/sessions';
import { hasDocDrag, readDocDrag, useDraggingDoc } from '@/pages/docs/dock/docDrag';
import { dropZoneClass } from '@/pages/docs/dock/dropZone';

/**
 * 대화 본문 — 말풍선·카드·입력창.
 *
 * <b>탭과 도크가 같은 것을 쓴다.</b> 두 벌로 만들면 한쪽만 고쳐지는 날이 온다.
 * 상태는 하나도 들고 있지 않다 — 전부 {@link useAgentChat} 에서 온다.
 *
 * <h2>`variant` 가 가르는 것</h2>
 *
 * <b>폭이 감당 못 하는 것들뿐이다.</b> 말풍선·입력창·확인 카드는 360px 에서도 그대로 선다.
 * 도면·공정율 곡선·회의 달력은 그 폭에 못 들어가므로 <b>접어서 탭으로 보낸다</b> — 좁은 폭용으로
 * 다시 그리지 않는다. 대화는 provider 에 있으니 탭으로 가도 그 자리 그대로다.
 */

/**
 * 빈 화면에 세울 제안 칩의 <b>상한</b> (2026-09-23 · DOC-94 §3 G).
 *
 * <p>좁은 쪽(도크 360px)이 기준이다 — 좌우 여백을 빼면 336px 이고, 한 줄짜리 예시
 * (「연차 며칠 남았어?」)는 글자 12px·좌우 여백 24px 이라 한 칩이 160~180px 이다. 곧
 * <b>한 줄에 둘</b>이고, 여섯이면 세 줄이다. 인사말 아래 세 줄까지가 도크에서 스크롤 없이 선다.
 *
 * <p>넓은 쪽(`/aiagent` 탭)에 맞춰 늘리지 않는다 — 탭에서 알맞은 수는 도크를 칩으로 덮는다.
 * 에이전트의 도메인은 열일곱이라 자르지 않으면 빈 화면이 목록이 된다.
 */
const SUGGESTION_LIMIT = 6;

/**
 * @param suggestions 빈 대화의 칩을 <b>이 목록으로</b> 바꾼다. 주지 않으면 에이전트가 준 목록이다.
 *        AI Agent 모드가 「세 칸이 다 움직이는」 발화만 골라 줄 때 쓴다({@link AgentModePage}).
 */
export function AgentChatView({ variant, suggestions: fixedSuggestions }: {
  variant: 'page' | 'dock';
  suggestions?: readonly string[];
}) {
  const navigate = useNavigate();
  const { data: session } = useSession();
  const userName = session?.user?.name ?? null;

  const {
    messages, draft, setDraft, isReplying, streaming, progress, failure, sendMessage, activeId, recordReceipt,
    rateMessage, pinnedDocs, pinDocument, unpinDocument,
    runAction, declineAction, actionPending,
    issuanceDraft, actionDraft, links, citations, calendar, floorplan, curve, probe, choice, ask,
  } = useAgentChat();

  const isDock = variant === 'dock';
  /** 아직 아무 말도 오가지 않은 빈 대화인가 — 제안 칩이 서는 자리다. */
  const isEmpty = messages.length === 0 && !streaming && !isReplying;
  /** 이 칸 <b>위에</b> 올라와 있는가(겨냥). */
  const [dragOver, setDragOver] = useState(false);
  /** 지금 자료실 파일을 끌고 있는가 — 시작하는 순간 이 칸이 켜진다({@link dropZoneClass}). */
  const draggingDoc = useDraggingDoc();
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // 새 메시지가 쌓이거나 대화를 바꾸면 맨 아래로 따라간다.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, streaming, isReplying]);

  // 답이 끝나면 입력으로 돌아온다. 대화를 바꾸거나 새로 시작할 때도 같은 자리다.
  useEffect(() => {
    if (!isReplying) textareaRef.current?.focus();
  }, [isReplying, activeId]);

  /**
   * 「메일로 열기」 가 클립보드에 담을 <b>본문</b> — 방금 온 답변이다 (DOC-110 D).
   *
   * <p>이벤트에 본문을 실어 보내지 않는 이유: 그 글자는 이미 화면에 있다. 한 번 더 실으면 같은
   * 본문이 두 벌이 되고, 흘러온 토큰과 완성문이 어긋난 턴에서는 <b>버튼이 옛 본문을 복사한다</b>.
   */
  const lastAnswer = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const message = messages[i];
      if (message?.role === 'assistant') return message.content;
    }
    return '';
  }, [messages]);

  const greeting = useMemo(
    () => (userName ? `${userName}님, 무엇을 도와드릴까요?` : '무엇을 도와드릴까요?'),
    [userName],
  );

  /*
    ## 제안 칩 — 빈 화면이 «무엇을 물으면 되나» 를 말한다 (2026-09-23 · DOC-94 §3 G)

    문구는 **에이전트가 준 것뿐이다**(`GET /v1/ai/capabilities`). 화면이 예시를 들고 있으면
    도구가 늘어도 화면만 옛 목록을 말한다 — `capabilities.ts` 머리말이 실제로 났던 그 일을
    적어 두었다. 서버가 비거나 실패하면 **아무것도 그리지 않는다.** 칩이 없는 빈 화면은
    예전 그대로고, 대화는 그대로 된다.
  */
  const { data: capabilities } = useAgentCapabilities(isEmpty && !fixedSuggestions);

  /**
   * 도메인마다 <b>첫 예시 하나씩</b>. 한 도메인의 예시를 여러 개 쌓으면 「에이전트가 할 수 있는 일」이
   * 아니라 「그 도메인」만 보인다 — 칩의 일은 폭을 보여 주는 것이다. 순서는 서버가 준 그대로 둔다.
   */
  const suggestions = useMemo(
    () => fixedSuggestions
      ? fixedSuggestions.slice(0, SUGGESTION_LIMIT)
      : (capabilities ?? [])
        .map((domain) => domain.examples?.[0])
        .filter((example): example is string => !!example)
        .slice(0, SUGGESTION_LIMIT),
    [capabilities, fixedSuggestions],
  );

  // Enter 로 보내고, Shift+Enter 로 줄바꿈한다 — 대화창의 관례다.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // 한글 입력 중의 Enter 는 글자 조합을 확정하는 키다. 그것까지 전송으로 받으면 확정 Enter 와
    // 전송 Enter 가 연달아 들어와 같은 말이 두 번 전송된다.
    if (event.nativeEvent.isComposing) return;

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void sendMessage();
    }
  };

  const canSend = draft.trim().length > 0 && !isReplying;

  /** 좁은 폭이 감당 못 하는 카드. 접어서 탭으로 보낸다. */
  const foldedCard = (label: string) => (
    <button
      onClick={() => navigate('/aiagent')}
      className="w-full px-3 py-2.5 rounded-xl bg-white hover:bg-slate-50 text-left"
      style={{ border: '1px dashed var(--border)' }}
    >
      <span className="block text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{label}</span>
      <span className="block text-[11px]" style={{ color: 'var(--text-muted)' }}>
        좁아서 접었습니다 · 눌러서 크게 보기
      </span>
    </button>
  );

  return (
    <>
      <div
        ref={scrollRef}
        className={`flex-1 overflow-y-auto ${isDock ? 'p-3 space-y-3' : 'p-4 sm:p-6 space-y-4'}`}
        style={{ background: 'var(--bg-hover)' }}
      >
        {isEmpty ? (
          <div className="h-full flex flex-col items-center justify-center gap-3 text-center">
            <div className={`${isDock ? 'w-11 h-11' : 'w-14 h-14'} rounded-full flex items-center justify-center bg-white`}>
              <svg width={isDock ? 20 : 24} height={isDock ? 20 : 24} viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" style={{ color: 'var(--accent)' }}>
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <p className={`font-semibold ${isDock ? 'text-sm px-3' : ''}`} style={{ color: 'var(--text-primary)' }}>
              {greeting}
            </p>

            {/* 제안 칩 — 누르면 그 문장이 <b>그대로 발화로</b> 나간다(`ChoiceCard` 와 같은 동작).
                서버가 준 것이 없으면 이 자리는 비어 있다. */}
            {suggestions.length > 0 && (
              <div className={`flex flex-wrap justify-center gap-1.5 ${isDock ? 'px-2' : 'px-4 max-w-[640px]'}`}>
                {suggestions.map((example) => (
                  <button
                    key={example}
                    onClick={() => void sendMessage(example)}
                    disabled={isReplying}
                    // 예시가 길어도 칩 하나가 줄을 통째로 밀지 않게 잘라 둔다 — 360px 에서
                    // 한 줄에 둘이 서는 것이 이 칩의 전제다(SUGGESTION_LIMIT).
                    className={`max-w-full truncate rounded-full bg-white hover:bg-slate-50 disabled:opacity-50 transition-colors ${isDock ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-1.5 text-sm'}`}
                    style={{ border: '1px solid var(--border)', color: 'var(--text-primary)' }}
                  >
                    {example}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          messages.map((message) => (
            <ChatBubble key={message.id} message={message} compact={isDock} onRate={rateMessage} />
          ))
        )}

        {/* 도착하는 중인 답변. 목록 맨 끝에만 붙는다. */}
        {streaming && (
          <ChatBubble
            message={{ id: 'streaming', role: 'assistant', content: streaming, createdAt: '' }}
            compact={isDock}
          />
        )}

        {isReplying && !streaming && <TypingBubble label={progress} />}

        {/* 근거 문서 — 답변 바로 아래. 눌러야 원본이 열린다(권한은 그때 백엔드가 다시 판정한다). */}
        <CitationCard citations={citations} />

        {/* 아래 셋은 그림이라 좁은 폭에 안 들어간다. 도크에서는 접고 탭으로 보낸다. */}
        {calendar ? (isDock ? foldedCard('회의 달력') : <MeetingCalendarCard payload={calendar} />) : null}
        {floorplan ? (isDock ? foldedCard('자리 배치도') : <FloorplanCard payload={floorplan} />) : null}
        {curve ? (isDock ? foldedCard('공정율 곡선') : <ProgressCurveCard payload={curve} />) : null}
        {/* 프로브 칸은 도크에서도 편다(DOC-109 §4). 접으면 값을 고쳐 다시 누르는 일 자체가 막힌다 —
            달력·곡선은 「그림」이라 접어도 답변 문장이 남지만, 이건 그 칸이 곧 기능이다. */}
        {probe ? <ProbeCard draft={probe} compact={isDock} /> : null}

        {/* 되물음 선택지. 누르면 그 문장이 **새 발화로** 나간다 — 실행되는 것은 없다. */}
        {choice ? <ChoiceCard payload={choice} onPick={sendMessage} disabled={isReplying} /> : null}

        {/* 슬롯 되물음의 보기(`ask`). 물음은 바로 위 말풍선에 이미 있어 버튼만 선다. */}
        {ask ? <ChoiceCard payload={ask} onPick={sendMessage} disabled={isReplying} /> : null}

        {/*
          에이전트가 넘겨준 화면.

          ★ 도크에서는 이 버튼이 **본문만 바꾸고 대화는 남긴다.** 도크의 존재 이유가 이 한 줄이다.
            탭에서는 예전과 같이 화면을 옮긴다(그쪽은 전체 화면이라 달리 방법이 없다).

          `guide` 버튼은 옮긴 뒤 그 화면의 사용 안내까지 편다. 눌렀을 때 무슨 일이 나는지가
          달라지므로 **모양도 다르다** — 물음표 하나가 그 차이를 말한다(DOC-20 §1.18).
        */}
        {links.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {links.map((link) => (
              link.href.startsWith('mailto:') ? (
                <MailLinkButton key={link.href} href={link.href} label={link.label} body={lastAnswer} compact={isDock} />
              ) : (
                <button
                  key={link.href}
                  onClick={() => navigate(link.guide ? withGuide(link.href) : link.href)}
                  className={`rounded-lg font-medium bg-white hover:bg-slate-50 ${isDock ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-1.5 text-sm'}`}
                  style={{ border: '1px solid var(--accent)', color: 'var(--accent)' }}
                >
                  {link.guide ? `? ${link.label}` : `${link.label} →`}
                </button>
              )
            ))}
          </div>
        )}

        {/* 쓰기 승낙 — 목록 맨 끝, 방금 한 말 바로 아래에 붙는다.
            발급만 미리보기 PDF 가 필요해 전용 카드를 쓰고, 나머지는 <b>에이전트의 문장</b>이
            값을 말한 뒤 이 줄이 답을 받는다(§5.4). */}
        {issuanceDraft && (
          <div className={isDock ? '' : 'max-w-[560px]'}>
            <IssuancePreviewCard draft={issuanceDraft} onExecuted={recordReceipt} />
          </div>
        )}
        {actionDraft && (
          <ConfirmRow
            spec={actionDraft.spec}
            pending={actionPending}
            onYes={() => void runAction()}
            onNo={() => declineAction()}
            compact={isDock}
          />
        )}
      </div>

      {/*
        입력칸은 <b>자료실 파일을 받는 자리</b>다(사용자 지시 2026-09-04). 도크에서 파일을 끌어다
        놓으면 그 문서를 참조로 걸고 묻는다 — 이름을 손으로 옮겨 적을 필요가 없다.

        <p>끌고 지나가는 동안에는 {@code types} 만 볼 수 있다({@code hasDocDrag} 머리말). 그래서
        「우리 것인가」 는 그 표지로 판정하고, 내용은 떨어진 뒤에 읽는다.
      */}
      <div
        /*
          끌기가 시작되는 순간 이 자리가 켜진다 — 대화는 <b>어떤 자료든</b> 참조로 받는다.

          <p>표시는 <b>안쪽</b>에 그린다({@code inset}). 이 칸은 도크 맨 아래·좌우 끝에 붙어 있어
          바깥으로 띄우면 삼면이 부모 밖에서 잘리고 <b>윗변만</b> 남는다(사용자 지적 2026-09-04).
        */
        className={`flex-shrink-0 space-y-2 ${isDock ? 'p-2.5' : 'p-3 sm:p-4'} ${dropZoneClass({ active: !!draggingDoc, over: dragOver, inset: true })}`}
        style={{ borderTop: '1px solid var(--border)' }}
        onDragOver={(event) => {
          if (!hasDocDrag(event.dataTransfer)) return;
          // 기본 동작을 막아야 «놓을 수 있는 자리» 가 된다. 안 막으면 브라우저가 파일을 그냥 연다.
          event.preventDefault();
          event.dataTransfer.dropEffect = 'copy';
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(event) => {
          const doc = readDocDrag(event.dataTransfer);
          setDragOver(false);
          if (!doc) return;
          event.preventDefault();
          pinDocument(doc);
        }}
      >
        {failure && <Notice tone="warning">{failure}</Notice>}

        {/* 끌어다 놓은 문서. 보내면 사라진다 — 그때 그 이름이 말풍선 첫 줄로 옮겨 간다. */}
        {pinnedDocs.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {pinnedDocs.map((doc) => (
              <span
                key={doc.ref}
                className="flex items-center gap-1 rounded-full px-2 py-0.5"
                style={{ background: 'var(--accent-bg)', color: 'var(--accent)', fontSize: 11, maxWidth: '100%' }}
                title={doc.ref}
              >
                <span className="truncate">📄 {doc.name}</span>
                <button onClick={() => unpinDocument(doc.ref)} aria-label={`${doc.name} 참조 빼기`} style={{ lineHeight: 1 }}>
                  ✕
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="flex items-end gap-1.5" data-tour={isDock ? undefined : 'page-form'}>
          <textarea
            ref={textareaRef}
            className="input-field flex-1 resize-none"
            style={
              isDock
                ? { minHeight: '42px', maxHeight: '120px', fontSize: '13px' }
                : { minHeight: '52px', maxHeight: '160px' }
            }
            placeholder={isDock ? '무엇이든 물어보세요' : '메시지를 입력하세요. (Enter 전송 · Shift+Enter 줄바꿈)'}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
          />
          <button
            onClick={() => void sendMessage()}
            disabled={!canSend}
            className="btn-primary"
            style={
              isDock
                ? { height: '42px', fontSize: '12px', padding: '0 12px', whiteSpace: 'nowrap' }
                : { height: '52px', whiteSpace: 'nowrap' }
            }
          >
            전송
          </button>
        </div>
      </div>
    </>
  );
}

/** 확인 카드가 실행된 뒤 남기는 영수증 줄의 머리(`recordReceipt`). 답변이 아니라 사실 기록이라 평가하지 않는다. */
const RECEIPT_PREFIX = '[실행됨]';

/**
 * @param onRate 있으면 저장된 답변 아래에 👍/👎 가 선다. 흘러오는 중인 말풍선은 넘기지 않는다 — 아직 id 가 없다.
 */
function ChatBubble({
  message, compact, onRate,
}: {
  message: AgentChatMessage;
  compact?: boolean;
  onRate?: (messageId: string, rating: AgentChatRating) => void;
}) {
  const isUser = message.role === 'user';
  const ratable = !isUser && !!onRate && !message.content.startsWith(RECEIPT_PREFIX);

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`rounded-2xl ${compact ? 'max-w-[88%] px-3 py-2' : 'max-w-[80%] px-4 py-2.5'}`}
        style={{
          background: isUser ? 'var(--accent)' : '#ffffff',
          color: isUser ? '#ffffff' : 'var(--text-primary)',
          border: isUser ? 'none' : '1px solid var(--border)',
          // 사용자 말풍선은 적은 그대로(줄바꿈 포함). 답변은 마크다운이 줄을 나눈다.
          whiteSpace: isUser ? 'pre-wrap' : 'normal',
          overflowWrap: 'anywhere',
          fontSize: compact ? '13px' : '14px',
          lineHeight: compact ? '1.65' : '1.7',
        }}
      >
        {isUser ? message.content : <Markdown text={message.content} />}
        {ratable && (
          <RatingButtons
            rating={message.rating ?? null}
            // 같은 것을 다시 누르면 되돌린다. 「누른 적 없음」과 「되돌림」은 서버에서도 같은 null 이다.
            onRate={(next) => onRate(message.id, message.rating === next ? null : next)}
          />
        )}
      </div>
    </div>
  );
}

/** 말풍선 오른쪽 아래의 👍/👎. 도크의 머리 아이콘과 같은 선 굵기·흐린 색이고, 고른 것만 강조색이다. */
function RatingButtons({ rating, onRate }: { rating: number | null; onRate: (next: 1 | -1) => void }) {
  const button = (value: 1 | -1, label: string, path: string) => {
    const selected = rating === value;
    return (
      <button
        type="button"
        onClick={() => onRate(value)}
        aria-label={label}
        aria-pressed={selected}
        title={label}
        className="p-1 rounded-md hover:bg-slate-100"
        style={{ color: selected ? 'var(--accent)' : 'var(--text-muted)', lineHeight: 0 }}
      >
        <svg
          width="13" height="13" viewBox="0 0 24 24" fill={selected ? 'currentColor' : 'none'}
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        >
          <path d={path} />
        </svg>
      </button>
    );
  };

  return (
    <div className="flex justify-end gap-0.5 mt-1 -mr-1.5 -mb-0.5">
      {button(1, '도움이 됐어요', 'M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.3a2 2 0 0 0 2-1.7l1.4-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3')}
      {button(-1, '도움이 안 됐어요', 'M10 15v4a3 3 0 0 0 3 3l4-9V2H5.7a2 2 0 0 0-2 1.7l-1.4 9a2 2 0 0 0 2 2.3zm7-13h3a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-3')}
    </div>
  );
}

/** 답이 오기 전의 자리. `label` 이 있으면 점 옆에 지금 무엇을 하는지 한 줄이 선다(`step` 이벤트). */
function TypingBubble({ label }: { label?: string | null }) {
  return (
    <div className="flex justify-start">
      <div className="rounded-2xl px-4 py-3 bg-white" style={{ border: '1px solid var(--border)' }}>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            {[0, 1, 2].map((index) => (
              <span
                key={index}
                className="w-1.5 h-1.5 rounded-full animate-pulse"
                style={{ background: 'var(--text-muted)', animationDelay: `${index * 0.2}s` }}
              />
            ))}
          </div>
          {label && <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{label}</span>}
        </div>
      </div>
    </div>
  );
}

/**
 * 「메일로 열기」 — AI 가 쓴 초안을 <b>그 사람의 메일 프로그램</b>에 띄운다 (DOC-110 D).
 *
 * <h2>왜 우리가 보내지 않나</h2>
 * 우리가 보내면 공용 발송 계정으로 나가 <b>받는 사람이 답장할 곳이 없고</b>, 보낸 사람의
 * 보낸편지함에도 남지 않는다. 열어 주면 개인 명의로 나가고 보낸편지함에 남으며, 발송 권한·한도·
 * 비밀번호가 하나도 필요 없다.
 *
 * <h2>본문은 클립보드로도 담는다</h2>
 * 윈도우는 {@code mailto:} 를 <b>명령줄로</b> 메일 프로그램에 넘겨 2,048자 안쪽이어야 하고,
 * 한글은 인코딩에서 한 글자가 아홉 자가 된다 — 초안 200자면 상한이다. 그래서 에이전트는 넘치는
 * 본문을 <b>주소에 싣지 않고</b>({@code _MAILTO_LIMIT}) 제목만 보낸다. 그 경우를 가리는 표시가
 * {@code &body=} 의 유무이고, 그때는 본문을 복사해 두고 <b>붙여 넣으라고 한 마디</b> 한다 —
 * 말해 주지 않으면 사람은 빈 메일 창을 보고 고장이라고 여긴다.
 *
 * <p>복사는 언제나 한다. 주소에 본문이 실린 경우에도 메일 프로그램이 자르는 날이 있고, 그때
 * 붙여 넣을 것이 손에 있는 편이 낫다.
 */
function MailLinkButton({
  href, label, body, compact,
}: { href: string; label: string; body: string; compact: boolean }) {
  /** 본문이 주소에 실리지 못해 <b>붙여 넣어야</b> 하는가. */
  const pasteNeeded = !href.includes('&body=');
  const [copied, setCopied] = useState(false);

  const open = async () => {
    if (body) {
      try {
        await navigator.clipboard.writeText(body);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2_500);
      } catch {
        /* 클립보드 권한이 없는 환경(비 HTTPS). 메일 창은 그대로 열고, 본문은 화면에 이미 있다. */
      }
    }
    window.location.href = href;
  };

  return (
    <button
      onClick={() => void open()}
      className={`rounded-lg font-medium bg-white hover:bg-slate-50 ${compact ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-1.5 text-sm'}`}
      style={{ border: '1px solid var(--accent)', color: 'var(--accent)' }}
    >
      {copied && pasteNeeded ? '본문 복사됨 — 붙여 넣으세요' : `✉ ${label}`}
    </button>
  );
}
